import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";
import { Shell, useMeta } from "@/components/Shell";
import { client } from "@/lib/api";

const LABELS = { pending: "Ödeme bekleniyor", test_success: "TEST işlemi doğrulandı; gerçek tahsilat yok", success: "PayTR ödemesi onaylandı", failed: "Ödeme başarısız", token_failed: "Ödeme başlatılamadı", amount_mismatch: "Tutar uyuşmazlığı" };
// Dönüş URL'inde takip kodu gelmediğinde aynı tarayıcı oturumundan okunur; kod üçüncü tarafa taşınmaz.
const codeKey = orderNo => `vezimza:paytr-code:${orderNo}`;
function rememberedCode(orderNo, fromUrl) {
  try {
    if (fromUrl) { sessionStorage.setItem(codeKey(orderNo), fromUrl); return fromUrl; }
    return sessionStorage.getItem(codeKey(orderNo)) || "";
  } catch { return fromUrl; }
}

const Bare = ({ children }) => <>{children}</>;

export default function Payment({ checkoutOrder = null, embedded = false, onCancelled = null }) {
  const meta = useMeta();
  const navigate = useNavigate();
  const autoStarted = useRef(false);
  const iframeRef = useRef(null);
  const route = useParams();
  const orderNo = checkoutOrder?.order_no || route.orderNo;
  const isKep = orderNo?.startsWith("KEP-");
  const paymentBase = isKep ? "/kep/payments" : "/payments/paytr";
  const trackPath = isKep ? "/kep/takip" : "/takip";
  const [params] = useSearchParams();
  const code = rememberedCode(orderNo, checkoutOrder?.verification_code || params.get("kod") || "");
  const [frame, setFrame] = useState(null), [state, setState] = useState(null), [error, setError] = useState(""), [busy, setBusy] = useState(false);
  const refresh = () => code && client.get(`${paymentBase}/status/${orderNo}`, { params: { verification_code: code } }).then(r => setState(r.data)).catch(() => {});
  useEffect(() => { refresh(); const t = setInterval(refresh, 6000); return () => clearInterval(t); }, [orderNo, code]); // eslint-disable-line
  async function start() {
    setBusy(true); setError("");
    try { const r = await client.post(`${paymentBase}/token`, { order_no: orderNo, verification_code: code }); setFrame(r.data.iframe_url); }
    catch (e) { setError(e.response?.data?.detail || "Ödeme başlatılamadı"); } finally { setBusy(false); }
  }
  useEffect(() => {
    if (!code || !state || (!checkoutOrder && params.get("baslat") !== "1") || params.get("sonuc") || autoStarted.current || state.order_status === "İptal edildi" || ["success", "test_success"].includes(state.paytr_status) || state.payment_status === "Başarılı") return;
    autoStarted.current = true;
    start();
  }, [code, state, params]); // eslint-disable-line
  useEffect(() => {
    if (!frame || !iframeRef.current) return;
    let active = true;
    const node = iframeRef.current;
    const connect = () => {
      if (active && window.iFrameResize && !node.iFrameResizer) {
        window.iFrameResize({ checkOrigin: ["https://www.paytr.com"], scrolling: true }, node);
      }
    };
    let script = document.getElementById("paytr-frame-resizer");
    if (!script) {
      script = document.createElement("script");
      script.id = "paytr-frame-resizer";
      script.src = "https://www.paytr.com/js/iframeResizer.min.js";
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", connect);
    connect();
    return () => {
      active = false;
      script.removeEventListener("load", connect);
      if (node.iFrameResizer) node.iFrameResizer.removeListeners();
    };
  }, [frame]);
  const cancelled = state?.order_status === "İptal edildi";
  const succeeded = ["success", "test_success"].includes(state?.paytr_status);
  const confirmed = !cancelled && succeeded && state?.payment_status === "Başarılı" && !state?.is_test;
  async function cancel() {
    setBusy(true); setError("");
    try { await client.post(`${paymentBase}/cancel`, {order_no:orderNo, verification_code:code}); setFrame(null); if (onCancelled) onCancelled(); else navigate(isKep ? "/kep" : "/paketler", {replace:true}); }
    catch (e) { setError(e.response?.data?.detail || "İptal gerçekleştirilemedi"); }
    finally { setBusy(false); }
  }
  const isTest = state ? !!state.is_test : meta?.payment_mode === "test";
  const Wrapper = embedded ? Bare : Shell;
  return <Wrapper><section className={embedded ? "checkout-payment" : "legal"} data-testid="payment-page">
    <div className="eyebrow">ÖDEME · PAYTR {isTest ? "TEST MODU" : ""}</div>
    <h1>{cancelled ? "Başvurunuz iptal edildi" : confirmed ? "Ödemeniz alındı. Teşekkür ederiz." : "Ödemenizi tamamlayın"}</h1><p>Sipariş kodu: {orderNo}</p>
    {state?.control_payment && <p className="notice" data-testid="control-payment-notice"><b>1,00 TL gerçek kontrol ödemesi.</b> Sertifika, donanım ve kargo içermez. Bu işlem yalnız ödeme akışını kontrol eder.</p>}
    <div className="notice" data-testid="payment-test-notice"><AlertTriangle size={14} /> {isTest ? <>PayTR <b>TEST</b> modundadır; gerçek tahsilat yapılmaz. </> : <>Ödemeniz PayTR üzerinden güvenli olarak alınır. </>} Ödeme tamamlandığında onayınız bu sayfada gösterilir.</div>
    {!code && <p className="error" data-testid="payment-code-missing">Bu sayfayı açmak için sipariş takip kodu gerekir. Takip sayfasından yönlendirilmeniz gerekiyor.</p>}
    {params.get("sonuc") && <p className="muted" data-testid="payment-redirect-note">PayTR yönlendirmesi: {params.get("sonuc") === "basarili" ? "ödeme formu tamamlandı" : "ödeme tamamlanmadı"} · kesin durum aşağıda görünür.</p>}
    {state && <p data-testid="payment-state"><b>Durum:</b> {LABELS[state.paytr_status] || "Ödeme başlatılmadı"} · Sipariş: {state.order_status} · Ödeme kaydı: {state.payment_status}{state.is_test ? " · TEST/SİMÜLASYON" : ""}</p>}
    {!cancelled && !frame && code && state && !succeeded && <button className="btn primary" disabled={busy} onClick={start} data-testid="payment-start-button"><ShieldCheck size={16} /> {busy ? "Hazırlanıyor…" : (isTest ? "PayTR ile öde (TEST)" : "PayTR ile öde")}</button>}
    {!cancelled && succeeded && <p className="notice" data-testid="payment-confirmed-note">{isTest ? "Test işlemi doğrulandı; gerçek tahsilat yapılmadı." : state.payment_status === "Başarılı" ? "Siparişiniz onaylandı. Sipariş sürecinizi takip sayfasından izleyebilirsiniz." : "Ödeme bildirimi alındı; sipariş ödeme kaydının güncellenmesi bekleniyor."}</p>}
    {cancelled && <Link className="btn ghost" to={isKep ? "/kep" : "/paketler"} data-testid="payment-return-link">Satış sayfasına dön</Link>}
    {cancelled && <p className="notice">İptal kaydınız alındı. Bankada başlamış bir ödeme varsa sonucu ayrıca kontrol edilir; bu işlem para iadesi yapmaz.</p>}
    {!cancelled && code && state && state.payment_status !== "Başarılı" && <button className="btn ghost" onClick={cancel} disabled={busy} data-testid="payment-cancel-button">Ödemeden vazgeç ve başvuruyu iptal et</button>}
    {error && <p className="error" data-testid="payment-error">{error}</p>}
    {!cancelled && frame && !succeeded && <div className="paytr-frame-shell" data-testid="payment-scroll-area"><iframe ref={iframeRef} title="PayTR ödeme formu" src={frame} id="paytriframe" frameBorder="0" scrolling="yes" style={{ width: "100%", height: 1100, minHeight: 600, display:"block", border:0 }} data-testid="payment-iframe" /></div>}
    <p style={{ marginTop: 24 }}><button className="btn ghost" onClick={refresh} data-testid="payment-refresh-button"><RefreshCw size={14} /> Durumu yenile</button> <Link className="btn ghost" to={trackPath} data-testid="payment-track-link">Sipariş takibine dön</Link></p>
  </section></Wrapper>;
}
